import {
  generators,
  referenceCaptureCommandSchema,
  referenceCaptureReplySchema,
  type MacroRegion,
  type PlaybackAssetGrant,
  type PlaycastMessage,
  type PlaycastMessageProcessMacroRun,
  type ReferenceCaptureCommand,
  type ReferenceCaptureReply,
} from '@playcastdotio/Messaging';

const COMMAND_TIMEOUT_MS = 5_000;
const SAMPLE_TIMEOUT_MS = 30_000;

export type ReferenceCapturePushResult = Extract<ReferenceCaptureReply, { status: 'captured' | 'sample' | 'failed' }>;
type ArmedReply = Extract<ReferenceCaptureReply, { status: 'armed' }>;
type MarkedReply = Extract<ReferenceCaptureReply, { status: 'marked' }>;
type EndedReply = Extract<ReferenceCaptureReply, { status: 'ended' }>;

interface PendingCommand {
  accept(reply: ReferenceCaptureReply): boolean;
  reject(error: Error): void;
  timer: ReturnType<typeof setTimeout>;
}

export interface INativeReferenceCaptureClient {
  begin(input: { sessionId: string; assets: PlaybackAssetGrant; outputPrefix: string }): Promise<ArmedReply>;
  mark(sessionId: string, markId: string, lookbackMs?: number, region?: MacroRegion): Promise<MarkedReply>;
  focus(sessionId: string, region: MacroRegion): void;
  end(sessionId: string): Promise<EndedReply>;
  /** sample and failed results in arrival order; a failed result without markId means the session ended natively. */
  subscribe(listener: (result: ReferenceCapturePushResult) => void): () => void;
  notify(input: unknown): boolean;
  dispose(): void;
}

/** Correlates `kind: 'reference-capture'` replies on the shared `process/macroRun` envelope. */
export class NativeReferenceCaptureClient implements INativeReferenceCaptureClient {
  private readonly listeners = new Set<(result: ReferenceCapturePushResult) => void>();
  private readonly pending = new Map<string, PendingCommand>();
  private readonly sampleTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Samples that arrived before their `marked` reply, keyed by markId. */
  private readonly earlyResults = new Set<string>();
  private disposed = false;

  public constructor(
    private readonly transport: (message: PlaycastMessage<PlaycastMessageProcessMacroRun>) => void,
    private readonly createId: () => string = () => crypto.randomUUID(),
  ) {}

  public begin(input: { sessionId: string; assets: PlaybackAssetGrant; outputPrefix: string }): Promise<ArmedReply> {
    return this.roundTrip({
      command: 'referenceCaptureBegin', requestId: this.createId(), sessionId: input.sessionId,
      assets: input.assets, outputPrefix: input.outputPrefix,
    }, (reply) => reply.status === 'armed' ? reply : undefined);
  }

  public async mark(sessionId: string, markId: string, lookbackMs?: number, region?: MacroRegion): Promise<MarkedReply> {
    const marked = await this.roundTrip({
      command: 'referenceCaptureMark', requestId: this.createId(), sessionId, markId,
      ...(lookbackMs !== undefined ? { lookbackMs } : {}),
      ...(region ? { region } : {}),
    }, (reply) => reply.status === 'marked' ? reply : undefined);
    this.watchSample(marked);
    return marked;
  }

  public focus(sessionId: string, region: MacroRegion): void {
    if (this.disposed) return;
    const parsed = referenceCaptureCommandSchema.safeParse({
      command: 'referenceCaptureFocus', requestId: this.createId(), sessionId, region,
    });
    if (!parsed.success) return;
    try { this.transport(generators.process.macroRun(parsed.data)); } catch { /* The next pointer move retries. */ }
  }

  public end(sessionId: string): Promise<EndedReply> {
    return this.roundTrip({
      command: 'referenceCaptureEnd', requestId: this.createId(), sessionId,
    }, (reply) => reply.status === 'ended' ? reply : undefined);
  }

  public subscribe(listener: (result: ReferenceCapturePushResult) => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  public notify(input: unknown): boolean {
    if (!claimsReferenceCapture(input)) return false;
    if (this.disposed) return true;
    const parsed = referenceCaptureReplySchema.safeParse(input);
    if (!parsed.success) return true;
    const reply = parsed.data;
    const accepted = this.pending.get(reply.requestId)?.accept(reply) ?? false;
    // Mark-scoped failures always reach subscribers. A failure with no markId that no command is waiting
    // for is Playjector ending the session on its own (source changed, device lost) after `armed`.
    if (reply.status === 'captured' || reply.status === 'sample'
      || (reply.status === 'failed' && (reply.markId || !accepted))) this.publish(reply);
    return true;
  }

  public dispose(): void {
    this.disposed = true;
    this.listeners.clear();
    for (const timer of this.sampleTimers.values()) clearTimeout(timer);
    this.sampleTimers.clear();
    for (const requestId of [...this.pending.keys()]) this.rejectPending(requestId, 'reference_capture_cancelled');
  }

  private roundTrip<T>(command: ReferenceCaptureCommand, matches: (reply: ReferenceCaptureReply) => T | undefined): Promise<T> {
    if (this.disposed) return Promise.reject(new Error('reference_capture_cancelled'));
    const parsed = referenceCaptureCommandSchema.safeParse(command);
    if (!parsed.success) return Promise.reject(new Error('invalid_command'));
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => this.rejectPending(command.requestId, 'native_reply_timeout'), COMMAND_TIMEOUT_MS);
      this.pending.set(command.requestId, {
        accept: (reply) => this.acceptReply(command.requestId, reply, matches, resolve, reject),
        reject,
        timer,
      });
      try { this.transport(generators.process.macroRun(parsed.data)); }
      catch { this.rejectPending(command.requestId, 'native_send_failed'); }
    });
  }

  private acceptReply<T>(requestId: string, reply: ReferenceCaptureReply, matches: (reply: ReferenceCaptureReply) => T | undefined,
    resolve: (value: T) => void, reject: (error: Error) => void): boolean {
    if (reply.status === 'failed') {
      this.clearPending(requestId);
      reject(new Error(reply.errorCode));
      return true;
    }
    const value = matches(reply);
    if (value === undefined) return false;
    this.clearPending(requestId);
    resolve(value);
    return true;
  }

  private watchSample(marked: MarkedReply): void {
    if (this.earlyResults.delete(marked.markId) || this.disposed) return;
    const timer = setTimeout(() => {
      this.sampleTimers.delete(marked.markId);
      if (this.disposed) return;
      this.publish({
        kind: 'reference-capture', requestId: marked.requestId, sessionId: marked.sessionId,
        status: 'failed', markId: marked.markId, errorCode: 'reference_capture_timeout',
      }, false);
    }, SAMPLE_TIMEOUT_MS);
    this.sampleTimers.set(marked.markId, timer);
  }

  private publish(result: ReferenceCapturePushResult, trackArrival = true): void {
    if (trackArrival && result.status !== 'captured' && result.markId) this.noteArrival(result.markId);
    for (const listener of this.listeners) {
      try { listener(result); } catch { /* One subscriber cannot drop the next result. */ }
    }
  }

  private noteArrival(markId: string): void {
    const timer = this.sampleTimers.get(markId);
    if (timer) { clearTimeout(timer); this.sampleTimers.delete(markId); return; }
    this.earlyResults.add(markId);
  }

  private rejectPending(requestId: string, errorCode: string): void {
    const pending = this.pending.get(requestId);
    if (!pending) return;
    this.clearPending(requestId);
    pending.reject(new Error(errorCode));
  }

  private clearPending(requestId: string): void {
    const pending = this.pending.get(requestId);
    if (!pending) return;
    clearTimeout(pending.timer);
    this.pending.delete(requestId);
  }
}

function claimsReferenceCapture(input: unknown): boolean {
  return !!input && typeof input === 'object' && 'kind' in input && input.kind === 'reference-capture';
}
